/**
 * Sale calendar `/{cc}/{lang}/sales` (web plan W0.15 / W3.4): every published
 * event hub, with its dates and where it stands, as plain links.
 *
 * W3.4: the event list is fetched from the backend (`saleEvents` query) so the
 * calendar reflects the backend's publish/unpublish state and year-switch dates.
 * The static `publishedSaleEvents()` (Pakistan's calendar) is kept as a fallback
 * while the backend response is in-flight or when the backend returns nothing;
 * another country has no static events, so it shows only what the backend lists.
 *
 * MW8: whether the country has a calendar at all is data (`locales
 * { hasSaleEvents }`, @/hooks/useSaleCountries): a country without published
 * events is the 404 page.
 */
import { lazy, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { SaleEventCard } from '@/components/sale/SaleEventCard';
import { SaleFollowCta } from '@/components/sale/SaleFollowCta';
import { SectionHeader } from '@/components/ui';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { useNow } from '@/hooks/useNow';
import { useSaleEventsList } from '@/hooks/useSaleEvent';
import { useSaleCountry } from '@/hooks/useSaleCountries';
import { PageHead } from '@/components/seo/PageHead';
import { useTranslate } from '@/i18n/LocaleLanguage';
import { DEFAULT_LOCALE, type Locale } from '@/seo/locales';
import { saleCalendarPageHead } from '@/seo/pageHeads';
import {
  mapBackendSaleEventRows,
  publishedSaleEvents,
  saleCalendarPath,
  saleEventPhase,
  type SaleEvent,
} from '@/seo/saleEvents';

function EventList({ events, now, locale }: { events: SaleEvent[]; now: Date; locale: Locale }) {
  return (
    <ul className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {events.map((event) => (
        <li key={event.slug}>
          <SaleEventCard event={event} now={now} locale={locale} />
        </li>
      ))}
    </ul>
  );
}

export interface SalesCalendarPageProps {
  /** The route's locale: routes are generated per locale with literal segments (App.tsx). */
  locale?: Locale;
}

const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

export default function SalesCalendarPage({ locale = DEFAULT_LOCALE }: SalesCalendarPageProps) {
  const { pathname, search, hash } = useLocation();
  const path = saleCalendarPath(locale);
  const saleCountry = useSaleCountry(locale);

  // W3.4: use backend event list; fall back to static config when unavailable.
  const { events: backendEvents } = useSaleEventsList(locale);
  const now = useNow(60_000);
  const events: SaleEvent[] = useMemo(() => {
    if (backendEvents) {
      return mapBackendSaleEventRows(backendEvents, now);
    }
    // The static config is Pakistan's calendar: another country never shows it.
    return locale.cc === DEFAULT_LOCALE.cc ? publishedSaleEvents() : [];
  }, [backendEvents, now, locale.cc]);
  // JSON-LD (web plan W0.7): a CollectionPage whose ItemList is every
  // published event hub, in date order. The SEO server sends the same head
  // (web plan W1.3).
  const t = useTranslate();
  const head = useMemo(() => saleCalendarPageHead(locale, { t, events }), [locale, t, events]);
  const next = events.filter((e) => saleEventPhase(e, now, locale) !== 'ended');
  const past = events.filter((e) => saleEventPhase(e, now, locale) === 'ended').reverse();

  // A country without published sale events has no calendar (src/seo/saleCountries.ts);
  // one whose flag is still on its way waits instead of flashing the 404 page.
  if (!saleCountry.enabled) return saleCountry.ready ? <NotFoundPage /> : <div aria-busy="true" className="min-h-[50vh]" />;

  // `/PK/EN/sales` or a trailing slash: the one lowercase URL.
  if (pathname !== path) return <Navigate replace to={{ pathname: path, search, hash }} />;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:py-8">
      <PageHead head={head} />
      <Breadcrumbs crumbs={head.breadcrumbs} className="mb-4" />
      <h1 className="text-2xl font-extrabold tracking-tight text-ink md:text-3xl">{head.h1}</h1>

      <div className="mt-8 space-y-12">
        {next.length > 0 && (
          <section aria-label="Next sales">
            <SectionHeader title="Next sales" />
            <EventList events={next} now={now} locale={locale} />
          </section>
        )}
        {past.length > 0 && (
          <section aria-label="Past sales">
            <SectionHeader title="Past sales" />
            <EventList events={past} now={now} locale={locale} />
          </section>
        )}
        <SaleFollowCta />
      </div>
    </div>
  );
}
